/**
 * @voltxampere/engine — Formula template: LED current limit (Sprint 56b)
 *
 * Subgraph: V_in → R → LED.anode → LED.cathode → GND.
 *
 *     V_in ── R ── LED.A ──[D]── LED.K ── GND
 *
 * Output: limiter current I_LED = (V_in − V_F) / R, with V_F set to
 * the LED's forward-voltage param (default 2.0 V if not provided).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { asNumber, componentsOfType, findVoltageSourceDriving, formatLatexValue, isGroundNet, netOf, } from "../helpers.js";
const SYMBOLIC = "I_{LED} = \\frac{V_{in} - V_F}{R}";
export const ledCurrentLimitTemplate = {
    id: "led-current-limit",
    specificity: "medium",
    displayNameKey: {
        tr: "formula.led-current-limit.name",
        en: "formula.led-current-limit.name",
    },
    match(circuit, selected) {
        const resistors = componentsOfType(circuit, "resistor", selected);
        const leds = componentsOfType(circuit, "led", selected);
        if (resistors.length === 0 || leds.length === 0)
            return null;
        for (const led of leds) {
            const anode = netOf(led, "anode");
            const cathode = netOf(led, "cathode");
            if (anode === null || cathode === null)
                continue;
            if (!isGroundNet(circuit, cathode))
                continue;
            for (const r of resistors) {
                const rP1 = netOf(r, "p1");
                const rP2 = netOf(r, "p2");
                if (rP1 === null || rP2 === null)
                    continue;
                let rIn = null;
                if (rP1 === anode)
                    rIn = rP2;
                else if (rP2 === anode)
                    rIn = rP1;
                if (rIn === null)
                    continue;
                const vSource = findVoltageSourceDriving(circuit, rIn);
                if (vSource === null)
                    continue;
                const rVal = asNumber(r.params.resistance);
                const vInVal = asNumber(vSource.params.voltage);
                // LED params may carry "vForward" / "forwardVoltage" / "vf";
                // fall back to 2.0 V if absent.
                const vfRaw = asNumber(led.params.vForward) ??
                    asNumber(led.params.forwardVoltage) ??
                    asNumber(led.params.vf) ??
                    2.0;
                if (rVal === null || vInVal === null || rVal === 0)
                    continue;
                const i = (vInVal - vfRaw) / rVal;
                if (!Number.isFinite(i) || i <= 0)
                    continue;
                const rLatex = formatLatexValue(rVal, "\\Omega");
                const vInLatex = formatLatexValue(vInVal, "V");
                const vfLatex = formatLatexValue(vfRaw, "V");
                const iLatex = formatLatexValue(i, "A");
                return {
                    templateId: "led-current-limit",
                    involvedComponents: [r.id, led.id, vSource.id],
                    involvedNets: [rIn, anode, cathode],
                    symbolic: SYMBOLIC,
                    numeric: {
                        formula: `I_{LED} = \\frac{${vInLatex} - ${vfLatex}}{${rLatex}} = ${iLatex}`,
                        result: i,
                        unit: "A",
                    },
                    specificity: "medium",
                };
            }
        }
        return null;
    },
};
//# sourceMappingURL=led-current-limit.js.map